/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useProcessesContext } from 'ogc-processes-components';
import { AntflowApiEndpoint } from '@/services/dataprovider';
import { ProjectCard } from '@/components/ProjectCard.tsx';
import { DataView } from 'primereact/dataview';
import { Project, ProjectList, ProjectSearchOptions } from '@/types/models.ts';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { useLocation, useNavigate } from 'react-router';
import { ProjectSearchBar } from '@/components/ProjectSearchBar';
import { FilterNavTopics } from '@/components/FilterNavTopics';
import { AppRoutes } from '@/constants/routes';
import { useStoreContext } from '@/contexts/StoreContext';

type PermalinkParams = {
  topics?: string[];
  search?: string;
};

type SearchOptions = ProjectSearchOptions & {
  fullUrl?: string;
};
export const ProjectListPage: React.FC<{ isSpace?: boolean }> = ({ isSpace = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { processesEndpoint } = useProcessesContext();
  const { antflowServerConfig, externalServiceConfig } = useStoreContext();
  const [filterInput, setFilterInput] = useState('');
  const RequestControllerRef = useRef<AbortController | null>(null);
  const [filteredTopics, setFilteredTopics] = useState<string[]>([]);
  const basePath = isSpace ? AppRoutes.spaces.url : AppRoutes.projects.url;

  const {
    data: searchListData,
    mutate: searchListMutate,
    isPending: searchListIsPending
  } = useMutation({
    mutationKey: ['search_projects', processesEndpoint],
    mutationFn: async (searchOptions: SearchOptions) => {
      const endpoint = processesEndpoint as AntflowApiEndpoint;
      if (RequestControllerRef.current) {
        RequestControllerRef.current.abort();
      }
      RequestControllerRef.current = new AbortController();
      if (searchOptions.fullUrl) {
        const projectList = await endpoint.get<ProjectList>(searchOptions.fullUrl, {
          signal: RequestControllerRef.current.signal
        });
        return projectList;
      }
      const projectList = await endpoint.search_projects(searchOptions, {
        signal: RequestControllerRef.current.signal
      });
      return projectList;
    }
  });

  /**
   * Apply search with current filter input and selected topics,
   * along with optional pagination parameters. If the search is empty and no topics are selected,
   * it fetches the default project list. Otherwise, it performs a search with the provided criteria.
   */
  const applySearch = useCallback(
    (searchOptions: SearchOptions) => {
      const { fullUrl, query: search, topics, cursor } = searchOptions;
      if (fullUrl) {
        searchListMutate({ fullUrl, query: search, topics: [] });
        return;
      }
      const projectTopic = isSpace ? externalServiceConfig?.spaces?.topic || '' : antflowServerConfig?.topic;
      const apiTopics = topics && topics.length > 0 ? Array.from(new Set([projectTopic, ...topics])) : [projectTopic];
      searchListMutate({ query: search.trim().length > 3 ? search : '', topics: apiTopics, cursor, limit: 12 });
    },
    [antflowServerConfig, externalServiceConfig, isSpace, searchListMutate]
  );

  useEffect(() => {
    if (location.search) {
      const searchParams = new URLSearchParams(location.search);
      const search = searchParams.get('search') || '';
      const topicsParam = searchParams.get('topics');
      const topics = topicsParam ? topicsParam.split(',') : [];
      setFilterInput(search);
      setFilteredTopics(topics);

      applySearch({ query: search, topics });
    } else {
      applySearch({ query: '', topics: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount to respect URL state

  const nextPage = useCallback(() => {
    applySearch({ query: '', topics: [], fullUrl: searchListData?.pagination?.next_page });
  }, [applySearch, searchListData?.pagination?.next_page]);

  const previousPage = useCallback(() => {
    applySearch({ query: '', topics: [], fullUrl: searchListData?.pagination?.prev_page });
  }, [applySearch, searchListData?.pagination?.prev_page]);

  const toQueryParams = useCallback((params: PermalinkParams) => {
    const urlParams = new URLSearchParams();
    if (params.search) {
      urlParams.set('search', params.search);
    }
    if (params.topics && params.topics.length > 0) {
      urlParams.set('topics', params.topics.join(','));
    }

    return urlParams.toString() ? `?${urlParams.toString()}` : undefined;
  }, []);

  const handleResetSearch = useCallback(() => {
    setFilterInput('');
    const permalink = toQueryParams({ search: '', topics: filteredTopics });
    applySearch({ query: '', topics: filteredTopics });
    navigate(`${basePath}${permalink ? permalink : ''}`);
  }, [toQueryParams, navigate, applySearch, filteredTopics, basePath]);

  const handleSearchInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFilterInput(value);
      if (value.trim().length > 2) {
        applySearch({ query: value, topics: filteredTopics });
      }
      if (value.trim().length === 0) {
        applySearch({ query: '', topics: filteredTopics });
      }
      const permalink = toQueryParams({ search: value, topics: filteredTopics });
      navigate(`${basePath}${permalink ? permalink : ''}`);
    },
    [filteredTopics, applySearch, toQueryParams, navigate, basePath]
  );

  const handleResetTopics = useCallback(() => {
    setFilteredTopics([]);
    const permalink = toQueryParams({ search: filterInput, topics: [] });
    applySearch({ query: filterInput, topics: [] });
    navigate(`${basePath}${permalink ? permalink : ''}`);
  }, [applySearch, filterInput, toQueryParams, navigate, basePath]);

  const addTopicToFilter = useCallback(
    (newTopic: string) => {
      setFilteredTopics((prev) => {
        const updatedTopics = Array.from(new Set([...prev, newTopic]));
        const permalink = toQueryParams({ search: filterInput, topics: updatedTopics });
        navigate(`${basePath}${permalink ? permalink : ''}`);
        applySearch({ query: filterInput, topics: updatedTopics });
        return updatedTopics;
      });
    },
    [filterInput, applySearch, toQueryParams, navigate, basePath]
  );

  const handleTopicChange = useCallback(
    (newTopics: string[]) => {
      setFilteredTopics(newTopics);
      const permalink = toQueryParams({ search: filterInput, topics: newTopics });
      navigate(`${basePath}${permalink ? permalink : ''}`);
      applySearch({ query: filterInput, topics: newTopics });
    },
    [filterInput, applySearch, toQueryParams, navigate, basePath]
  );

  const listTemplate = (items: Project[]) => {
    if (!items || items.length === 0) {
      return null;
    }

    const list = items.map((project, index) => {
      return (
        <ProjectCard
          isSpace={isSpace}
          onFilterOnTopics={addTopicToFilter}
          className="col-12 sm:col-12 md:col-6 lg:col-4 xl:col-3 px-1 my-2"
          key={index}
          project={project}
        />
      );
    });

    return <div className="grid grid-nogutter justify-content-start">{list}</div>;
  };

  return (
    <div className="h-full flex">
      {/* Main Content */}
      <div className="flex-grow-1 flex flex-column overflow-hidden">
        <div className="w-full flex justify-content-between mb-2">
          <ProjectSearchBar
            className="w-full border-round-xl"
            size={30}
            isLoading={searchListIsPending}
            value={filterInput}
            onChange={handleSearchInputChange}
            onClear={handleResetSearch}
            placeholder={t(['project.search_placeholder', 'Search'])}
          />
        </div>
        <FilterNavTopics
          selectedTopics={filteredTopics}
          onTopicChange={handleTopicChange}
          onClearAll={handleResetTopics}
        />

        <div
          style={{ minHeight: '38rem' }}
          className="flex flex-column w-full justify-content-between overflow-auto h-full pt-4 pr-2 "
        >
          <DataView
            pt={{ content: { className: '' } }}
            className="w-full"
            loading={searchListIsPending}
            value={searchListData?.projects || []}
            listTemplate={listTemplate}
          />
          {searchListData?.projects && searchListData.projects.length > 0 && (
            <div className="flex align-items-center justify-content-center gap-4 mt-4 mb-4">
              <Button
                size="small"
                text
                label={t(['actions.previous', 'Previous'])}
                iconPos="left"
                icon="pi pi-angle-left"
                onClick={previousPage}
                disabled={!searchListData?.pagination?.prev_page || searchListIsPending}
              />
              <Button
                size="small"
                text
                label={t(['actions.next', 'Next'])}
                iconPos="right"
                icon="pi pi-angle-right"
                onClick={nextPage}
                disabled={!searchListData?.pagination?.next_page || searchListIsPending}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
